/**
 * Le cycle du budget : mensuel (mois calendaire) ou hebdomadaire (lundi→dimanche).
 *
 * Toutes les bornes sont des dates ISO AAAA-MM-JJ, INCLUSES des deux côtés.
 * On travaille en heure locale et jamais en UTC : `toISOString()` décalerait
 * d'un jour toute saisie faite après 21 h à Madagascar (UTC+3).
 */
import type { Cycle } from '../db/database'
import { formatMonth, formatShortDate, toIsoDate } from './dates'

export interface CycleRange {
  start: string
  end: string
}

function parse(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d)
}

function shift(date: Date, days: number): Date {
  const next = new Date(date)
  next.setDate(date.getDate() + days)
  return next
}

/** Le cycle qui contient cette date. */
export function cycleRange(iso: string, cycle: Cycle): CycleRange {
  const date = parse(iso)

  if (cycle === 'weekly') {
    // getDay() met dimanche à 0 ; ici la semaine commence le lundi.
    const offset = (date.getDay() + 6) % 7
    const start = shift(date, -offset)
    return { start: toIsoDate(start), end: toIsoDate(shift(start, 6)) }
  }

  const start = new Date(date.getFullYear(), date.getMonth(), 1)
  // Jour 0 du mois suivant = dernier jour de celui-ci. Gère février et les
  // années bissextiles sans table de longueurs de mois.
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0)
  return { start: toIsoDate(start), end: toIsoDate(end) }
}

export function nextCycle(range: CycleRange, cycle: Cycle): CycleRange {
  return cycleRange(toIsoDate(shift(parse(range.end), 1)), cycle)
}

export function previousCycle(range: CycleRange, cycle: Cycle): CycleRange {
  return cycleRange(toIsoDate(shift(parse(range.start), -1)), cycle)
}

export function contains(range: CycleRange, iso: string): boolean {
  return iso >= range.start && iso <= range.end
}

/** Toutes les dates du cycle, dans l'ordre. */
export function daysOf(range: CycleRange): string[] {
  const days: string[] = []
  for (let date = parse(range.start); toIsoDate(date) <= range.end; date = shift(date, 1)) {
    days.push(toIsoDate(date))
  }
  return days
}

/**
 * Les cycles depuis `from` jusqu'à celui qui contient `to`, bornes comprises.
 * Sert au cumul des enveloppes cumulatives (l'épargne).
 */
export function cyclesUpTo(from: string, to: string, cycle: Cycle): CycleRange[] {
  const last = cycleRange(to, cycle)
  const ranges: CycleRange[] = []
  let current = cycleRange(from, cycle)

  while (current.start <= last.start) {
    ranges.push(current)
    current = nextCycle(current, cycle)
    // Garde-fou : une date aberrante ne doit pas faire tourner la boucle sans fin.
    if (ranges.length > 2000) break
  }
  return ranges
}

/**
 * Répartit un montant entier sur N jours, **le dernier jour absorbant le reste**
 * (décision vision.md).
 *
 * L'Ariary n'a pas de décimale : 100 000 sur 30 jours donne 3 333, et
 * 3 333 × 30 = 99 990. Sans cette règle, l'app perdrait 10 Ar sous les yeux de
 * l'utilisateur — et une app d'argent qui perd de l'argent à l'écran perd sa
 * crédibilité. La somme rendue ici vaut TOUJOURS exactement `amount`.
 */
export function splitEvenly(amount: number, days: number): number[] {
  if (days <= 0) return []
  const base = Math.floor(amount / days)
  const parts = Array<number>(days).fill(base)
  parts[days - 1] += amount - base * days
  return parts
}

/**
 * Le cycle en toutes lettres : « août 2026 », « semaine du 17 au 23 août ».
 *
 * Sans ce repère, l'utilisateur ne sait pas de quelle période il gère l'argent —
 * et sur une app dont tout dépend du cycle, c'est la première question qu'il se
 * pose en ouvrant l'écran.
 */
export function cycleLabel(range: CycleRange, cycle: Cycle): string {
  if (cycle === 'monthly') return formatMonth(range.start)
  // Même mois : on ne répète pas « août » deux fois dans la même phrase.
  const sameMonth = range.start.slice(0, 7) === range.end.slice(0, 7)
  return sameMonth
    ? `semaine du ${Number(range.start.slice(8))} au ${formatShortDate(range.end)}`
    : `semaine du ${formatShortDate(range.start)} au ${formatShortDate(range.end)}`
}

/** Le libellé porté par le revenu d'un cycle, à l'ouverture comme au renouvellement. */
export function cycleIncomeLabel(cycle: Cycle): string {
  return cycle === 'monthly' ? 'Revenu du mois' : 'Revenu de la semaine'
}

/**
 * Le cycle après « de », contracté : « d'août 2026 », « de mars 2026 »,
 * « de la semaine du 17 au 23 août ».
 *
 * Distinct de `cycleLabelDefined`, qui produit « LE MOIS d'août » : celui-là ne
 * se recolle pas après « de » sans donner « de le mois ». Une app qui parle
 * français avant d'être traduite ne peut pas se permettre ce genre de couture.
 */
export function cycleLabelSince(range: CycleRange, cycle: Cycle): string {
  const label = cycleLabel(range, cycle)
  if (cycle === 'weekly') return `de la ${label}`
  // Élision devant voyelle : « d'août », mais « de mars ».
  return /^[aeiouâéèêîôû]/i.test(label) ? `d'${label}` : `de ${label}`
}

/**
 * Le cycle prêt à s'insérer dans une phrase : « le mois d'août 2026 », « la
 * semaine du 17 au 23 août ».
 *
 * `cycleLabel` seul ne se recolle à rien — « Le point sur semaine du 17 au 23
 * août » n'est pas du français, et l'app parle français avant d'être traduite.
 */
export function cycleLabelDefined(range: CycleRange, cycle: Cycle): string {
  if (cycle === 'weekly') return `la ${cycleLabel(range, cycle)}`
  const label = cycleLabel(range, cycle)
  // Élision devant voyelle : « le mois d'août », mais « le mois de mars ».
  return /^[aeiouâéèêîôû]/i.test(label) ? `le mois d'${label}` : `le mois de ${label}`
}
